import { z } from 'zod';
import { OperationIdSchema, PlayerIdSchema } from '../../ids';
import { ItemAmountsSchema } from '../../models/items';
import { InventorySchema, ObjectIdSchema, ObjectViewSchema } from '../../models/inventory';
import {
  ContractHistorySchema,
  ContractViewSchema,
  QuestEntrySchema,
  QuestIdSchema,
  QuestLogSchema,
  QuestOfferSchema,
} from '../../models/quest';
import { API_PREFIX, defineEndpoint } from '../endpoint';

/** A quest giver's id (QUEST technical 0): a point of the layout (format only). */
const GiverIdSchema = z.string().regex(/^[a-z][a-z0-9_]{0,47}$/);

/** The character's quest log (QUEST-LOG): its chains with their step, count and hint. */
export const getQuestLog = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/me/quests`,
  access: 'session',
  response: QuestLogSchema,
});

/** What a giver offers the character, standing within its reach (`quest_not_here` otherwise). */
export const getQuestOffers = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/quests/offered`,
  access: 'session',
  query: z.object({ giver: GiverIdSchema }),
  response: z.object({ giver: GiverIdSchema, quests: z.array(QuestOfferSchema) }),
});

/**
 * Speaks to a giver about a chain, standing within its reach: takes it (or takes it up again where
 * it was left), or does its current step when the step is this giver's (`talk`, or `deliver` from
 * the backpack). Its rewards are paid in the same transaction (QU-4; coins its power's treasury
 * lacks are owed, QU-6). Idempotent by operation id.
 */
export const talkQuest = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/quests/talk`,
  access: 'session',
  body: z.object({ operationId: OperationIdSchema, giver: GiverIdSchema, quest: QuestIdSchema }),
  response: z.object({
    operationId: OperationIdSchema,
    quest: QuestEntrySchema,
    /** Coins paid now and owed by the treasury until its day (QU-6). */
    coins: z.object({ paid: z.number().int().min(0), owed: z.number().int().min(0) }),
    inventory: InventorySchema,
  }),
});

/** Sets a chain aside: it counts nothing until taken up again, where it was. Idempotent. */
export const abandonQuest = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/me/quests/:quest/abandon`,
  access: 'session',
  params: z.object({ quest: QuestIdSchema }),
  body: z.object({ operationId: OperationIdSchema }),
  response: z.object({ operationId: OperationIdSchema, quest: QuestEntrySchema }),
});

/** A board's id (QUEST-BOARD): a point of the layout (format only). */
const BoardIdSchema = z.string().regex(/^[a-z][a-z0-9_]{0,47}$/);

/** What a board offers the character, standing within its reach (`quest_not_here` otherwise). */
export const getBoard = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/boards/:board`,
  access: 'session',
  params: z.object({ board: BoardIdSchema }),
  response: z.object({
    board: BoardIdSchema,
    contracts: z.array(ContractViewSchema),
    /** What contracts left for the character at this board (JC-1), taken out in person. */
    vault: ItemAmountsSchema.optional(),
    /** The unique objects among it (a contract's reward, JC-8). */
    vaultObjects: z.array(ObjectViewSchema).optional(),
  }),
});

/**
 * Takes a contract of a board, standing by it (QU-16: one goes to find them); what it asks counts
 * from then on (B2). Refused once it closed or its slots are gone (`contract_closed`). Idempotent.
 */
export const takeContract = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/boards/:board/take`,
  access: 'session',
  params: z.object({ board: BoardIdSchema }),
  body: z.object({ operationId: OperationIdSchema, contract: z.string().min(1).max(64) }),
  response: z.object({ operationId: OperationIdSchema, contract: ContractViewSchema }),
});

const ContractIdSchema = z.string().min(1).max(64);

/**
 * Publishes a delivery or a bounty on a board, standing by it (ECON-CONTRACT C3a, C3b, KT-1):
 * `items` brought to this board, or `defeat`s of a creature, pay `coins` from the treasury and the
 * goods `pays` and objects `paysObjects` from the backpack, all kept in the contract from now on
 * (KT-3, JC-6), with a listing fee paid always (JC-2, JC-9). Every taker has `termMs` of game time,
 * during which its publisher cannot cancel it (JC-7); an exclusive one has one taker at a time,
 * leaving `guarantee` coins (KT-4). Seen only on this board (JC-3). Idempotent.
 */
export const publishContract = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/boards/:board/contracts`,
  access: 'session',
  params: z.object({ board: BoardIdSchema }),
  body: z
    .object({
      operationId: OperationIdSchema,
      /**
       * A delivery: these goods brought to this board. With `to`, a transport (C3c): these goods,
       * from the backpack, sealed in a crate its taker carries to the board `to`.
       */
      items: ItemAmountsSchema.optional(),
      to: BoardIdSchema.optional(),
      /** A bounty (C3b): `count` defeats of a creature of `sheet`, or of `family`. */
      defeat: z
        .object({
          sheet: z.string().min(1).max(64).optional(),
          family: z.string().min(1).max(64).optional(),
          count: z.number().int().min(1).max(100),
        })
        .refine((d) => (d.sheet === undefined) !== (d.family === undefined))
        .optional(),
      coins: z.number().int().min(0).max(1_000_000),
      /** Goods and unique objects it pays besides coin (JC-6), from the backpack. */
      pays: ItemAmountsSchema.optional(),
      paysObjects: z.array(ObjectIdSchema).max(8).optional(),
      exclusive: z.boolean(),
      guarantee: z.number().int().min(0).max(1_000_000).default(0),
      termMs: z.number().int().positive().optional(),
    })
    .refine((b) => (b.items === undefined) !== (b.defeat === undefined), {
      message: 'a contract asks goods or defeats: one of them',
    })
    .refine((b) => b.to === undefined || (b.items !== undefined && b.exclusive), {
      message: 'a transport carries goods, and is exclusive',
    })
    .refine(
      (b) =>
        b.coins > 0 || Object.keys(b.pays ?? {}).length > 0 || (b.paysObjects?.length ?? 0) > 0,
      { message: 'a contract pays something' },
    ),
  response: z.object({ operationId: OperationIdSchema, contract: ContractViewSchema }),
});

/**
 * Brings goods from the backpack to a delivery, standing by its board: they stay the bringer's
 * until it is complete (and can be withdrawn); completing it pays the bringer at once, and what
 * was brought waits for the publisher in its vault at the board (JC-1). Idempotent.
 */
export const deliverContract = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/boards/:board/deliver`,
  access: 'session',
  params: z.object({ board: BoardIdSchema }),
  body: z.object({
    operationId: OperationIdSchema,
    contract: ContractIdSchema,
    items: ItemAmountsSchema,
  }),
  response: z.object({ operationId: OperationIdSchema, contract: ContractViewSchema }),
});

/** Takes back to the backpack what one brought to a delivery not yet complete. Idempotent. */
export const withdrawDelivery = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/boards/:board/withdraw`,
  access: 'session',
  params: z.object({ board: BoardIdSchema }),
  body: z.object({ operationId: OperationIdSchema, contract: ContractIdSchema }),
  response: z.object({ operationId: OperationIdSchema, contract: ContractViewSchema }),
});

/**
 * Cancels one's own contract, standing by its board: only untaken and with nothing brought to it
 * (`contract_in_progress`). Its coins go back; its listing deposit is lost (JC-2). Idempotent.
 */
export const cancelContract = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/boards/:board/cancel`,
  access: 'session',
  params: z.object({ board: BoardIdSchema }),
  body: z.object({ operationId: OperationIdSchema, contract: ContractIdSchema }),
  response: z.object({ operationId: OperationIdSchema }),
});

/**
 * Takes goods and objects out of one's vault at a board into the backpack, standing by it (JC-1,
 * JC-8). Idempotent.
 */
export const takeFromBoardVault = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/boards/:board/vault`,
  access: 'session',
  params: z.object({ board: BoardIdSchema }),
  body: z.object({
    operationId: OperationIdSchema,
    items: ItemAmountsSchema.default({}),
    objects: z.array(ObjectIdSchema).max(32).default([]),
  }),
  response: z.object({
    operationId: OperationIdSchema,
    vault: ItemAmountsSchema,
    vaultObjects: z.array(ObjectViewSchema),
  }),
});

/** A character's record as a contractor (KT-3), seen by anyone. */
export const getContractHistory = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/characters/:playerId/contracts`,
  access: 'session',
  params: z.object({ playerId: PlayerIdSchema }),
  response: ContractHistorySchema,
});

/**
 * Hands a transport's sealed crate in at its destination board, standing by it (C3c): whoever
 * carries it, its taker or not (TT-1), is paid; what it held waits for the publisher in its vault
 * there. Idempotent.
 */
export const deliverCargo = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/boards/:board/unseal`,
  access: 'session',
  params: z.object({ board: BoardIdSchema }),
  body: z.object({ operationId: OperationIdSchema, contract: ContractIdSchema }),
  response: z.object({ operationId: OperationIdSchema, contract: ContractViewSchema }),
});
